import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Dialog, H2, Input, Label, Paragraph, XStack, YStack } from 'tamagui';
import { defaultUnitForCountry, type UnitSystem } from '@cartrack/shared';
import { api, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import {
  detectReminderPreset,
  presetDescriptions,
  unitSuffix,
  useMe,
  useUpdateMe,
  type ReminderPresetName,
} from '../lib/user-prefs';
import { LocationFields, type LocationValue } from '../components/LocationFields';
import { ThemeToggle } from '../components/ThemeToggle';

export function Settings() {
  const { session, signOut } = useAuthStore();
  const navigate = useNavigate();
  const { me, isLoading } = useMe();
  const updateMe = useUpdateMe();
  const unit = me?.unitSystem ?? 'metric';
  const [showDelete, setShowDelete] = useState(false);
  const [typedEmail, setTypedEmail] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [unitNote, setUnitNote] = useState<UnitSystem | null>(null);

  const deleteMut = useMutation({
    mutationFn: () => api.deleteMe(),
    onSuccess: async () => {
      await signOut();
      navigate('/login', { replace: true });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        const body = err.body as { code?: string; message?: string } | null;
        if (body?.code === 'LAST_ADMIN') {
          setDeleteError(body.message ?? 'You are the only remaining admin.');
          return;
        }
      }
      setDeleteError(err instanceof Error ? err.message : String(err));
    },
  });

  const [location, setLocation] = useState<LocationValue>({
    country: 'US',
    stateProvince: null,
    postalCode: null,
  });
  useEffect(() => {
    if (me) {
      setLocation({
        country: me.country,
        stateProvince: me.stateProvince,
        postalCode: me.postalCode,
      });
    }
  }, [me]);
  const locationDirty =
    me !== undefined &&
    (location.country !== me.country ||
      (location.stateProvince ?? null) !== (me.stateProvince ?? null) ||
      (location.postalCode ?? null) !== (me.postalCode ?? null));

  return (
    <YStack gap="$4">
      <H2>Settings</H2>

      <Card padding="$4" elevate>
        <YStack gap="$2">
          <Paragraph>
            <strong>Email:</strong> {session?.user.email}
          </Paragraph>
          <Paragraph size="$2" color="$color10">
            <strong>User ID:</strong> {session?.user.id}
          </Paragraph>
        </YStack>
      </Card>

      <Card padding="$4" elevate>
        <YStack gap="$3">
          <Paragraph fontWeight="700">Units</Paragraph>
          <Paragraph size="$2" color="$color10">
            Choose how distances are displayed. Your odometer readings are stored in whichever
            unit you pick — pick the one that matches your car.
          </Paragraph>
          {isLoading ? (
            <Paragraph color="$color10">Loading…</Paragraph>
          ) : (
            <XStack gap="$2">
              <Button
                theme={unit === 'metric' ? 'blue' : undefined}
                onPress={() => updateMe.mutate({ unitSystem: 'metric' })}
                disabled={updateMe.isPending}
              >
                Kilometres (km)
              </Button>
              <Button
                theme={unit === 'imperial' ? 'blue' : undefined}
                onPress={() => updateMe.mutate({ unitSystem: 'imperial' })}
                disabled={updateMe.isPending}
              >
                Miles (mi)
              </Button>
            </XStack>
          )}
          {updateMe.isError ? (
            <Paragraph color="$red10">Failed to update. Try again.</Paragraph>
          ) : null}
        </YStack>
      </Card>

      <Card padding="$4" elevate>
        <YStack gap="$3">
          <Paragraph fontWeight="700">Appearance</Paragraph>
          <Paragraph size="$2" color="$color10">
            Switch between light and dark. Your choice is remembered on this device.
          </Paragraph>
          <XStack>
            <ThemeToggle variant="inline" />
          </XStack>
        </YStack>
      </Card>

      <Card padding="$4" elevate>
        <YStack gap="$3">
          <Paragraph fontWeight="700">Location</Paragraph>
          <Paragraph size="$2" color="$color10">
            Used to correlate vehicle performance with climate and regional factors. Only
            country, state/province, and postal code are stored — no street address.
          </Paragraph>
          {isLoading ? (
            <Paragraph color="$color10">Loading…</Paragraph>
          ) : (
            <>
              <LocationFields value={location} onChange={setLocation} />
              <XStack gap="$2" justifyContent="flex-end">
                <Button
                  theme={locationDirty ? 'blue' : undefined}
                  disabled={!locationDirty || updateMe.isPending}
                  onPress={() => {
                    const nextUnit = defaultUnitForCountry(location.country);
                    const unitChanged =
                      me !== undefined &&
                      location.country !== me.country &&
                      nextUnit !== me.unitSystem;
                    setUnitNote(unitChanged ? nextUnit : null);
                    updateMe.mutate({
                      country: location.country,
                      stateProvince: location.stateProvince,
                      postalCode: location.postalCode,
                      ...(unitChanged ? { unitSystem: nextUnit } : {}),
                    });
                  }}
                >
                  {updateMe.isPending ? 'Saving…' : 'Save location'}
                </Button>
              </XStack>
              {unitNote ? (
                <Paragraph size="$2" color="$color10">
                  Switched to {unitNote === 'metric' ? 'kilometres' : 'miles'} for this
                  country. You can change this under Units above.
                </Paragraph>
              ) : null}
            </>
          )}
        </YStack>
      </Card>

      <Card padding="$4" elevate>
        <YStack gap="$3">
          <Paragraph fontWeight="700">Reminders</Paragraph>
          <Paragraph size="$2" color="$color10">
            When to nudge you about upcoming maintenance. In-app only, no email. Logging
            a service automatically stops the repeat for that item.
          </Paragraph>
          {isLoading || !me ? (
            <Paragraph color="$color10">Loading…</Paragraph>
          ) : (
            <>
              <XStack gap="$2" flexWrap="wrap">
                {(['relaxed', 'standard', 'tight'] as ReminderPresetName[]).map((preset) => {
                  const active = detectReminderPreset(me.reminders, unit) === preset;
                  return (
                    <Button
                      key={preset}
                      theme={active ? 'blue' : undefined}
                      disabled={updateMe.isPending}
                      onPress={() => updateMe.mutate({ reminderPreset: preset })}
                    >
                      {preset[0]!.toUpperCase() + preset.slice(1)}
                      {preset === 'standard' ? ' (default)' : ''}
                    </Button>
                  );
                })}
              </XStack>
              <Paragraph size="$2" color="$color10">
                {presetDescriptions(unit)[
                  (detectReminderPreset(me.reminders, unit) === 'custom'
                    ? 'standard'
                    : detectReminderPreset(me.reminders, unit)) as ReminderPresetName
                ]}
              </Paragraph>
              <Paragraph size="$1" color="$color10">
                Current: {me.reminders.leadDays} days or{' '}
                {me.reminders.leadMiles.toLocaleString()} {unitSuffix(unit)} lead time, repeat
                every {me.reminders.repeatWeeks}{' '}
                {me.reminders.repeatWeeks === 1 ? 'week' : 'weeks'},
                {' '}follow-up {me.reminders.followUpEnabled ? 'on' : 'off'} at{' '}
                {me.reminders.followUpDays} days past due.
              </Paragraph>
            </>
          )}
        </YStack>
      </Card>

      <Card padding="$4" elevate borderColor="$red8" borderWidth={1}>
        <YStack gap="$3">
          <Paragraph fontWeight="700" color="$red11">
            Danger zone
          </Paragraph>
          <Paragraph size="$2" color="$color10">
            Deleting your account permanently removes your vehicles, service logs,
            maintenance schedule, mileage history, notifications, uploaded receipts, and
            sign-in. This cannot be undone.
          </Paragraph>
          <XStack justifyContent="flex-end">
            <Button theme="red" onPress={() => {
              setTypedEmail('');
              setDeleteError(null);
              setShowDelete(true);
            }}>
              Delete my account
            </Button>
          </XStack>
        </YStack>
      </Card>

      <Dialog
        modal
        open={showDelete}
        onOpenChange={(o) => (o ? undefined : setShowDelete(false))}
      >
        <Dialog.Portal>
          <Dialog.Overlay key="overlay" opacity={0.5} />
          <Dialog.Content
            key="content"
            elevate
            padding="$4"
            width={460}
            maxWidth="90vw"
            gap="$3"
          >
            <Dialog.Title>Delete account</Dialog.Title>
            <Paragraph size="$2">
              This permanently deletes your account and all associated data. It cannot
              be undone.
            </Paragraph>
            <Paragraph size="$2" color="$color10">
              To confirm, type your email address below:
            </Paragraph>
            <Paragraph size="$1" color="$color11" fontWeight="600">
              {me?.email ?? ''}
            </Paragraph>
            <Label>Email</Label>
            <Input
              value={typedEmail}
              onChangeText={setTypedEmail}
              placeholder="your@email.com"
              autoCapitalize="none"
              autoComplete="email"
            />
            {deleteError ? (
              <Paragraph color="$red10">{deleteError}</Paragraph>
            ) : null}
            <XStack gap="$2" justifyContent="flex-end">
              <Button
                onPress={() => setShowDelete(false)}
                disabled={deleteMut.isPending}
              >
                Cancel
              </Button>
              <Button
                theme="red"
                disabled={
                  deleteMut.isPending ||
                  !me ||
                  typedEmail.trim().toLowerCase() !== me.email.toLowerCase()
                }
                onPress={() => deleteMut.mutate()}
              >
                {deleteMut.isPending ? 'Deleting…' : 'Delete forever'}
              </Button>
            </XStack>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>
    </YStack>
  );
}
